/* PortfolioDonutCard — Fáze 3 portfolio insight card.
 *
 * Visual:
 *  ┌────────────────────────────────────┐
 *  │ 🔘 PORTFOLIO KLIENTA               │
 *  │                                    │
 *  │      ╭───────╮                     │
 *  │     ╱  AUM    ╲      Investice 1.2M│
 *  │    │ 2 400 000 │     Penze     450k│
 *  │     ╲   Kč    ╱      Pojištění 280k│
 *  │      ╰───────╯      Rezerva   470k│
 *  │                                    │
 *  │  ┌──────────────┐ ┌───────────────┐│
 *  │  │ ROČNÍ VÝNOS  │ │ PROVIZE YTD   ││
 *  │  │ 6.2 %        │ │ 42 800 Kč     ││
 *  │  └──────────────┘ └───────────────┘│
 *  └────────────────────────────────────┘
 *
 * AUM = Assets Under Management. Buckets:
 *   investice  — invest + stavebni
 *   penze      — penzijko + duchodove
 *   pojisteni  — life / car / property / health
 *   rezerva    — explicit reserves (emergency fund)
 *
 * Debts (hypoteka / uver) are EXCLUDED from AUM but kept as separate
 * info — they still drive yield (0%) and commission calc.
 */
import { useMemo, useState } from "react";
import { motion } from "motion/react";
import { KROK, MAX_KROKU, RYCHLE, EASE_OUT, useBezPohybu } from "@/lib/pohyb";
/* Graf z @evilcharts (CRM-16, pravidlo CLAUDE.md), ne holý Recharts. */
import { EvilPieChart } from "@/components/evilcharts/charts/recharts-pie-chart";
import { PieChart as PieIcon } from "lucide-react";
import SoftSection from "@/components/crm/SoftSection";
import { useLanguage } from "@/contexts/LanguageContext";
import { desetinne, celeCislo } from "@/lib/i18n/format";
import { bjNaPenize, umiBjNaPenize, hlaskaBezKoeficientu } from "@/lib/bjPenize";


/* AUM category meta — colors come from active theme tokens.
 * 19. 10. 2026: kategorie produktů = tokeny skupin `--skup-*` (stejný význam jako skupiny
 * kalkulaček); `--cat-b/--cat-c` jsou semafor (pozor/špatně) a pro kategorii se nehodí. */
const CATEGORY_ORDER = ["investice", "penze", "pojisteni", "rezerva"];
const CATEGORY_META = {
  investice: {
    label: "Investice",
    cssVar: "--skup-investice",
    matches: ["investice", "stavebni"],
  },
  penze: {
    label: "Penze",
    cssVar: "--skup-penze",
    matches: ["penzijko", "duchodove"],
  },
  pojisteni: {
    label: "Pojištění",
    cssVar: "--skup-pojisteni",
    matches: ["pojisteni_zivot", "pojisteni_auta", "pojisteni_majetek", "pojisteni_odpovednost", "pojisteni_ostatni", "zdravotni"],
  },
  rezerva: {
    label: "Rezerva",
    cssVar: "--info",
    matches: ["rezerva"],
  },
};

/* Contract types EXCLUDED from AUM (debts, etc.) */
const DEBT_TYPES = new Set(["hypoteka", "uver"]);

function bucketFor(type) {
  const t = (type || "").toLowerCase();
  for (const key of CATEGORY_ORDER) {
    if (CATEGORY_META[key].matches.includes(t)) return key;
  }
  return null; // unknown type — skipped from AUM
}

function aggregate(contracts) {
  const buckets = Object.fromEntries(
    CATEGORY_ORDER.map((key) => [
      key,
      { key, name: CATEGORY_META[key].label, klic: key, cssVar: CATEGORY_META[key].cssVar, value: 0, count: 0 },
    ])
  );
  (contracts || []).forEach((c) => {
    const t = (c.type || "").toLowerCase();
    if (DEBT_TYPES.has(t)) return;
    const bk = bucketFor(c.type);
    if (!bk) return;
    const v = Number(c.total_amount) || (Number(c.monthly_amount) || 0) * 12;
    if (!v) return;
    buckets[bk].value += v;
    buckets[bk].count += 1;
  });
  return CATEGORY_ORDER.map((k) => buckets[k]);
}

/**
 * Estimate annual yield (rough heuristic for UI display; replace with real
 * portfolio model when backend ships it). Debts not included.
 */
function estimateYield(contracts) {
  if (!contracts || contracts.length === 0) return null;
  let weighted = 0;
  let total = 0;
  contracts.forEach((c) => {
    const t = (c.type || "").toLowerCase();
    if (DEBT_TYPES.has(t)) return;
    const v = Number(c.total_amount) || (Number(c.monthly_amount) || 0) * 12;
    if (!v) return;
    let est = 0.02;
    if (t === "investice" || t === "stavebni") est = 0.062;
    else if (t === "penzijko" || t === "duchodove") est = 0.045;
    else if (t.startsWith("pojisteni") || t === "zdravotni") est = 0.018;
    weighted += v * est;
    total += v;
  });
  if (total === 0) return null;
  return (weighted / total) * 100;
}

/**
 * Estimate commission YTD (placeholder until backend wires commission tracking).
 * 1.2% of all managed contracts (incl. mortgage broker fee).
 */
function estimateCommissionYtd(contracts) {
  let total = 0;
  (contracts || []).forEach((c) => {
    const v = Number(c.total_amount) || (Number(c.monthly_amount) || 0) * 12;
    total += v;
  });
  return total * 0.012;
}

export default function PortfolioDonutCard({ contracts = [], signedBj = 0, koef = 87.5, testid = "portfolio-donut" }) {
  /* Texty přes slovník `portfolioKarta.*`, částky v locale a měně TENANTA
   * (velká kontrola 5. 10. 2026, T1-10) — dřív napevno cs-CZ a „Kč“. */
  const { lang, t, currency } = useLanguage();
  const sym = currency?.symbol || "Kč";
  const fmtKc = (n) => new Intl.NumberFormat(currency?.locale || "cs-CZ").format(Math.round(n || 0));
  const dataSurova = useMemo(() => aggregate(contracts), [contracts]);
  const data = useMemo(() => dataSurova.map((b) => ({ ...b, name: t(`portfolioKarta.${b.klic}`, b.name) })), [dataSurova, t]);
  const [hoverIdx, setHoverIdx] = useState(null);

  const aum = data.reduce((s, b) => s + b.value, 0);
  /* Odhad z BJ jen u korunového tenanta (koeficient je Kč/BJ); u SK žádný
   * „26 250 €*“ (CRM-SK-01, rozhodnutí majitele 19. 10. 2026). */
  const fallbackAum = bjNaPenize(signedBj, koef, currency?.code) || 0;
  const ukazProvizi = umiBjNaPenize(currency?.code);
  const total = aum > 0 ? aum : fallbackAum;
  const isEmpty = total <= 0;
  const aumKnown = aum > 0;

  const bezPohybu = useBezPohybu();
  // Only non-zero slices go into the donut. Memo = stabilní props, ať se koláč při hoveru
  // (změna stavu) nekreslí znovu od nuly.
  const donutData = useMemo(() => data.filter((b) => b.value > 0), [data]);
  /* EvilPieChart: config/data přes useMemo — nová reference = koláč se roztočí znovu. */
  const donutConfig = useMemo(() => Object.fromEntries(donutData.map((b) => {
    const c = `var(${b.cssVar})`;
    return [b.key, { label: b.name, colors: { light: [c], dark: [c] } }];
  })), [donutData]);
  const donutRows = useMemo(() => donutData.map((b) => ({ name: b.key, value: b.value })), [donutData]);
  const pieProps = useMemo(() => (bezPohybu ? { isAnimationActive: false } : undefined), [bezPohybu]);

  const yieldPct = useMemo(() => estimateYield(contracts), [contracts]);
  const commissionYtd = useMemo(() => estimateCommissionYtd(contracts), [contracts]);

  const activeBucket = hoverIdx !== null ? data[hoverIdx] : null;

  return (
    <SoftSection title={t("portfolioKarta.titul", "Portfolio klienta")} icon={PieIcon} testid={testid} padding="px-3 pb-3 pt-2">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
        {/* Donut on the left (col-5) */}
        <div className="md:col-span-5 relative h-[148px]" data-testid={`${testid}-chart`}>
          {!aumKnown ? (
            <div
              className="h-full w-full flex flex-col items-center justify-center rounded-md text-center px-3"
              style={{ background: "var(--surface-2)", border: "1px dashed var(--line)" }}
            >
              <div className="text-popisek uppercase tracking-[0.14em] font-bold" style={{ color: "var(--muted)" }}>
                AUM
              </div>
              {/* Bez smluv žádné „0 Kč" — nezadáno není nula (CRM-16). */}
              <div className="font-display text-[16px] mt-1 tabular-nums" style={{ color: "var(--ink)", fontWeight: 700 }}>
                {isEmpty ? "—" : `${fmtKc(total)} ${sym}*`}
              </div>
              <div className="text-drobny mt-1" style={{ color: "var(--muted)" }}>
                {isEmpty ? t("portfolioKarta.zadnyProdukt", "Žádný investiční produkt") : t("portfolioKarta.odhadBj", "* odhad z podepsaných BJ")}
              </div>
            </div>
          ) : (
            <>
              <EvilPieChart config={donutConfig} data={donutRows} dataKey="value" nameKey="name" className="h-full w-full aspect-auto">
                <EvilPieChart.Pie
                  innerRadius={42}
                  outerRadius={64}
                  paddingAngle={donutData.length > 1 ? 2 : 0}
                  cornerRadius={3}
                  startAngle={90}
                  endAngle={-270}
                  pieProps={pieProps}
                />
              </EvilPieChart>
              {/* Center label */}
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                <div className="text-popisek uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--muted)" }}>
                  {activeBucket && activeBucket.value > 0 ? activeBucket.name : "AUM"}
                </div>
                <div
                  className="font-display tabular-nums leading-tight mt-0.5"
                  style={{ color: "var(--ink)", fontSize: 17, fontWeight: 700, letterSpacing: "-0.02em" }}
                >
                  {fmtKc(activeBucket && activeBucket.value > 0 ? activeBucket.value : aum)}
                </div>
                <div className="text-drobny" style={{ color: "var(--muted)" }}>{sym}</div>
              </div>
            </>
          )}
        </div>

        {/* Legend on the right (col-7) — always shows all 4 buckets */}
        <div className="md:col-span-7 space-y-1">
          {data.map((b, i) => {
            const pct = aumKnown && aum > 0 ? (b.value / aum) * 100 : 0;
            const active = hoverIdx === i;
            const zero = b.value === 0;
            return (
              <motion.button
                key={b.key}
                initial={bezPohybu ? false : { opacity: 0, x: -4 }}
                animate={{ opacity: 1, x: 0 }}
                transition={bezPohybu ? { duration: 0 } : { duration: RYCHLE, ease: EASE_OUT, delay: Math.min(i, MAX_KROKU) * KROK }}
                onMouseEnter={() => setHoverIdx(i)}
                onMouseLeave={() => setHoverIdx(null)}
                className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md transition-colors text-left"
                style={{
                  background: active ? "var(--surface-2)" : "transparent",
                  opacity: zero ? 0.45 : 1,
                }}
                data-testid={`${testid}-legend-${b.key}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                    style={{ background: `var(${b.cssVar})` }}
                  />
                  <span className="text-drobny font-medium truncate" style={{ color: "var(--ink)" }}>{b.name}</span>
                  {b.count > 0 && (
                    <span className="text-drobny" style={{ color: "var(--muted)" }}>{b.count}×</span>
                  )}
                </div>
                <div className="flex items-baseline gap-1.5 flex-shrink-0">
                  <span className="text-drobny tabular-nums" style={{ color: "var(--ink)" }}>{aumKnown ? fmtKc(b.value) : "—"}</span>
                  <span className="text-drobny tabular-nums" style={{ color: "var(--muted)" }}>
                    {aumKnown && aum > 0 ? `${celeCislo(pct, lang)}%` : "—"}
                  </span>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Bottom tiles: Roční výnos + Provize YTD */}
      <div className="grid grid-cols-2 gap-2 mt-3">
        {/* „6.0 %" byl `toFixed(1)` puštěný rovnou do UI — americká tečka
            v klientské složce. Oddělovač volí jazyk (lib/i18n/format.js). */}
        <MetricTile
          label={t("portfolioKarta.rocniVynos", "Roční výnos")}
          value={yieldPct !== null && yieldPct > 0 ? `${desetinne(yieldPct, 1, lang)} %` : "—"}
          accent={yieldPct !== null && yieldPct > 0 ? "positive" : "neutral"}
          testid={`${testid}-yield`}
        />
        <MetricTile
          label={t("portfolioKarta.proviceYtd", "Provize YTD")}
          value={ukazProvizi && commissionYtd > 0 ? `${fmtKc(commissionYtd)} ${sym}` : "—"}
          accent={ukazProvizi && commissionYtd > 0 ? "money" : "neutral"}
          testid={`${testid}-commission`}
          title={ukazProvizi ? undefined : hlaskaBezKoeficientu(lang)}
        />
      </div>
    </SoftSection>
  );
}

/* Výnos = dobrý stav (--positive), provize = peníze (--money). Text v tmavším odstínu
 * (`-text`, ≥ 4,5 : 1); dřív `${color}33` — z `var(--…)33` vznikla neplatná barva. */
function MetricTile({ label, value, accent = "accent", testid, title }) {
  const ton = {
    positive: { color: "var(--positive-text)", line: "var(--positive)", bg: "var(--positive-soft)" },
    money: { color: "var(--money-text)", line: "var(--money, var(--positive))", bg: "color-mix(in srgb, var(--money, var(--positive)) 12%, var(--surface))" },
    /* Bez dat (—) neutrálně — zelená plocha by tvrdila úspěch, kde nic není (CRM-15). */
    neutral: { color: "var(--muted)", line: "var(--line)", bg: "var(--surface-2)" },
  }[accent] || { color: "var(--accent)", line: "var(--accent)", bg: "var(--accent-soft)" };
  const { color } = ton;
  return (
    <div
      className="rounded-md px-3 py-2 flex items-center justify-between"
      style={{ background: ton.bg, border: `1px solid color-mix(in srgb, ${ton.line} 20%, transparent)` }}
      data-testid={testid}
      title={title}
    >
      <div className="text-popisek uppercase tracking-[0.12em] font-bold" style={{ color: "var(--muted)" }}>
        {label}
      </div>
      <div className="font-display tabular-nums" style={{ color, fontSize: 14, fontWeight: 700, letterSpacing: "-0.01em" }}>
        {value}
      </div>
    </div>
  );
}
